import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  findingsAdminApi,
  isDeployJobActive,
  workerApi,
  type CopilotFindingDto,
} from '../../api/findings';
import { formatApiError } from '../../utils/errors';

/**
 * Admin triage board for the copilot findings registry (/admin/findings).
 * One screen, two planes: the cross-workspace ticket table (priority ▲▼,
 * reject, markdown export) and the dev worker plane — the "Diegimai"
 * section: undeployed commits + Deploy dabar (left), deploy job history
 * with expandable output tails (right), plus the heartbeat chip.
 * 20 s refetch keeps both live without sockets.
 *
 * Finding 14 (2026-09-11): ProductBug/Enhancement rows carry a checkbox
 * persisting an auto-deploy mark on the server (PATCH /auto-deploy) — the
 * BACKEND watches the marks: when every marked finding is Fixed and the
 * system is idle, it queues the deploy job itself, and once the commits
 * ship it flips the marked findings to Done („Įdiegta"). The board only
 * shows the marks and a small status line in Diegimai — closing the page
 * no longer disarms the watch.
 *
 * Finding 15 (2026-09-11): the watch is MODE-aware and fully server-owned —
 * worker status carries autoDeployMode: All (server default; every Fixed
 * ProductBug/Enhancement finding ships when idle, then flips to Done, or to
 * Failed with the deploy error in its Progresas tail), Marked (the finding
 * 14 opt-in checkboxes — the only mode where the board shows them) and Off
 * (no automation). The board is a read-only surface over the mode.
 */

const CATEGORY_BADGE: Record<string, string> = {
  ClientPattern: 'bg-amber-100 text-amber-700',
  ProductBug: 'bg-red-100 text-red-700',
  Enhancement: 'bg-primary-50 text-primary-700',
};

/** Deploy-job status chip: in-flight amber, shipped green, failed red, queued gray. */
const DEPLOY_STATUS_CHIP: Record<string, string> = {
  Queued: 'bg-gray-100 text-gray-600',
  Running: 'bg-amber-100 text-amber-700',
  Succeeded: 'bg-green-100 text-green-700',
  Failed: 'bg-red-100 text-red-700',
};

/**
 * MySQL DATETIME values come back with Kind=Unspecified — serialized WITHOUT
 * the Z suffix — and JS would parse them as LOCAL time, shifting ages by the
 * viewer's timezone (the EEST +3h „3 valandų senumo" bug on a brand-new
 * finding). Anything without an explicit offset is UTC by contract.
 */
function parseAsUtc(iso: string): Date {
  return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

/** Age of a finding: whole days, hours under a day (triage scale, not minutes). */
function ageLabel(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = Date.now() - parseAsUtc(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return t('findings.ageDays', { days });
  return t('findings.ageHours', { hours: Math.max(0, Math.floor(ms / 3_600_000)) });
}

/** Age of the last worker progress post: seconds under a minute, then minutes. */
function progressAgeLabel(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - parseAsUtc(iso).getTime()) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min`;
}

/** Relative age of a deploy job: seconds → minutes → findings.ageHours → findings.ageDays. */
function jobAgeLabel(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = Math.max(0, Date.now() - parseAsUtc(iso).getTime());
  if (ms < 60_000) return `${Math.floor(ms / 1000)} s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return t('findings.ageHours', { hours: Math.floor(ms / 3_600_000) });
  return t('findings.ageDays', { days: Math.floor(ms / 86_400_000) });
}

/** No worker report for 10 minutes = the worker is gone or the commander hangs. */
const PROGRESS_STALE_MS = 10 * 60_000;

export default function FindingsBoard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Expanded progress panels, keyed by finding id (rows re-render on the 20 s poll).
  const [progressOpen, setProgressOpen] = useState<Record<number, boolean>>({});
  // Expanded deploy-job output tails, keyed by job id.
  const [outputOpen, setOutputOpen] = useState<Record<number, boolean>>({});

  // Status + list on the same 20 s heartbeat — the board is a watchtower.
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['worker-status'],
    queryFn: workerApi.status,
    refetchInterval: 20_000,
    retry: false,
  });
  const { data: findings, isLoading } = useQuery({
    queryKey: ['admin-findings'],
    queryFn: () => findingsAdminApi.list(),
    refetchInterval: 20_000,
  });

  const priorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: number; priority: number }) =>
      findingsAdminApi.setPriority(id, priority),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) =>
      findingsAdminApi.setStatus(id, next),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  // DEPLOY-AS-TASK (owner redesign 2026-09-11): the button creates a HIGH-priority
  // deploy FINDING (priority 1) — the commander picks it up like any other task
  // and runs the deploy. No direct infrastructure execution from the UI.
  const deployMutation = useMutation({
    mutationFn: () => findingsAdminApi.deployNow(),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
      await queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  const heartbeat = status?.heartbeat;
  const secondsSince = heartbeat?.lastSeenUtc
    ? Math.max(0, Math.floor((Date.now() - new Date(heartbeat.lastSeenUtc).getTime()) / 1000))
    : null;
  const workerOnline = !!heartbeat?.connected && secondsSince !== null;

  const deployJobs = status?.deployJobs ?? [];
  const deployActive = deployJobs.some(isDeployJobActive);
  // Undeployed commits from the heartbeat (max 15, newest first); an older
  // worker or a not-yet-loaded status → nothing ready → the all-deployed line.
  const undeployed = heartbeat?.undeployed ?? [];


  // The ACTIVE list is the work; solved items are history. Default hides the
  // closed (Fixed/Done/Rejected) behind a count toggle — the owner rule:
  // "everything still in one list" (2026-09-10).
  const isClosed = (f: CopilotFindingDto) =>
    f.status === 'Fixed' || f.status === 'Done' || f.status === 'Rejected';
  const [showClosed, setShowClosed] = useState(false);
  const allFindings = findings ?? [];
  const closedCount = allFindings.filter(isClosed).length;
  const activeFindings = allFindings.filter(f => !isClosed(f));
  // Owner rule 2026-09-12: deploy tasks auto-create via heartbeat when Fixed
  // findings exist; the button is a manual override — active ONLY when there
  // are deployables AND no deploy task already sitting in the queue.
  const hasDeployables = allFindings.some(f => f.status === 'Fixed'
    && (f.category === 'ProductBug' || f.category === 'Enhancement'));
  const deployTaskQueued = allFindings.some(f =>
    f.status === 'Queued' && f.title.startsWith('ĮDIEGTI:'));
  // Deploy-as-task history: findings with ĮDIEGTI: title (any status — the
  // old DeployJobStore is retired; the findings registry is the source).
  const deployTaskHistory = allFindings
    .filter(f => f.title.startsWith('ĮDIEGTI:'))
    .slice(0, 10);
  const visibleFindings = showClosed ? allFindings : activeFindings;

  // ── Auto-deploy marks (finding 14, 2026-09-11) ───────────────────────────
  // The checkbox PERSISTS a mark on the server; the backend owns the watch
  // (fire the deploy when all marked findings are Fixed + idle, flip them to
  // Done after it ships). Fixed is the key state to mark (fix done, waiting
  // to ship); Queued→Failed may also be marked so they ship when fixed.
  // ClientPattern rows never qualify — that lane produces no deployable
  // product changes (mirrors the backend entity rule).
  const isAutoDeployEligible = (f: CopilotFindingDto) =>
    (f.category === 'ProductBug' || f.category === 'Enhancement') &&
    f.status !== 'Done' && f.status !== 'Rejected';

  // One PATCH per mark toggle; the 20 s refetch (plus the invalidate below)
  // brings back the server-persisted autoDeploy flags.
  const autoDeployMutation = useMutation({
    mutationFn: ({ id, autoDeploy }: { id: number; autoDeploy: boolean }) =>
      findingsAdminApi.setAutoDeploy(id, autoDeploy),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  // The marked set feeding the Diegimai status line: Done findings already
  // shipped (the backend flips them), so only non-Done marks are "in flight".
  const autoMarked = allFindings.filter(f => f.autoDeploy && f.status !== 'Done');
  const autoWaitingCount = autoMarked.filter(f => f.status !== 'Fixed').length;

  // ── Auto-deploy mode (finding 15, 2026-09-11) ─────────────────────────────
  // The server owns the watch; the board only mirrors the mode. A missing
  // mode (older API) = Marked, so today's checkbox UI keeps working.
  // Checkboxes exist ONLY in Marked mode — in All/Off the marks are
  // meaningless (everything eligible ships anyway / nothing ships).
    // Auto-queue settings (owner ask 2026-09-12: per-category checkmarks)
  const { data: autoQueue } = useQuery({
    queryKey: ['auto-queue'],
    queryFn: findingsAdminApi.getAutoQueue,
    retry: false,
  });
  const autoQueueMutation = useMutation({
    mutationFn: (settings: { enhancement?: boolean; productBug?: boolean; clientPattern?: boolean }) =>
      findingsAdminApi.setAutoQueue(settings),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auto-queue'] }),
    onError: (err: Error) => setError(err.message),
  });

const autoDeployMode = status?.autoDeployMode ?? 'Marked';
  const marksVisible = autoDeployMode === 'Marked';
  // All-mode readout counts: Fixed deployable findings waiting to ship, and
  // Failed findings (an AUTO deploy failure flips them to Failed — their
  // progress.logTail carries the deploy error).
  const modeAllWaitingCount = allFindings.filter(
    f => f.status === 'Fixed' && (f.category === 'ProductBug' || f.category === 'Enhancement'),
  ).length;
  const modeAllFailedCount = allFindings.filter(f => f.status === 'Failed').length;

  // Header select-all: tri-state over the VISIBLE eligible rows. Clicking
  // marks all when any eligible row is unmarked, else unmarks all — one
  // sequential PATCH per changed row (admin board, few rows).
  const visibleEligible = visibleFindings.filter(isAutoDeployEligible);
  const allEligibleMarked =
    visibleEligible.length > 0 && visibleEligible.every(f => f.autoDeploy);
  const noEligibleMarked = visibleEligible.every(f => !f.autoDeploy);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (selectAllRef.current)
      selectAllRef.current.indeterminate = !allEligibleMarked && !noEligibleMarked;
  }, [allEligibleMarked, noEligibleMarked]);
  const toggleSelectAll = async () => {
    const mark = !allEligibleMarked; // any unmarked row → mark all
    for (const f of visibleEligible) {
      if (f.autoDeploy !== mark) {
        await autoDeployMutation.mutateAsync({ id: f.id, autoDeploy: mark });
      }
    }
  };

  const exportFinding = (id: number) => {
    findingsAdminApi.export(id).catch(err => setError(err instanceof Error ? err.message : t('findings.actionFailed')));
  };

  const statusLabel = (s: string) => {
    const known = ['Registered', 'Queued', 'PickedUp', 'InProgress', 'Fixed', 'Failed', 'Done', 'Rejected'];
    return known.includes(s) ? t(`findings.status.${s}`) : s;
  };
  const catLabel = (c: string) =>
    c === 'ClientPattern' || c === 'ProductBug' || c === 'Enhancement' ? t(`findings.cat.${c}`) : c;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('findings.title')}</h1>
        {/* Worker chip — connection recency from the heartbeat */}
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full ${
            heartbeat?.scriptStale
              ? 'bg-amber-50 text-amber-700'
              : workerOnline ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${workerOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
          {statusLoading
            ? t('common.loading')
            : heartbeat?.scriptStale
              ? t('findings.worker.stale')
              : workerOnline
                ? t('findings.worker.online', { seconds: secondsSince })
                : t('findings.worker.offline')}
        </span>
        {/* Auto-queue per category (owner ask 2026-09-12) */}
        <div className="flex items-center gap-3 text-xs">
          {autoQueue && (
            <>
              <label className="flex items-center gap-1 cursor-pointer" title="Automatiškai į eilę: Patobulinimai">
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked={autoQueue.enhancement}
                  disabled={autoQueueMutation.isPending}
                  onChange={(e) => autoQueueMutation.mutate({ enhancement: e.target.checked })}
                />
                <span className="text-gray-500">Auto ⚡ Patobulinimai</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer" title="Automatiškai į eilę: Sistemos klaidos">
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked={autoQueue.productBug}
                  disabled={autoQueueMutation.isPending}
                  onChange={(e) => autoQueueMutation.mutate({ productBug: e.target.checked })}
                />
                <span className="text-gray-500">Auto ⚡ Sistemos klaidos</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer" title="Automatiškai į eilę: Kliento taisyklės">
                <input
                  type="checkbox"
                  className="accent-primary-600"
                  checked={autoQueue.clientPattern}
                  disabled={autoQueueMutation.isPending}
                  onChange={(e) => autoQueueMutation.mutate({ clientPattern: e.target.checked })}
                />
                <span className="text-gray-500">Auto ⚡ Kliento taisyklės</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Dev plane — "Diegimai": ready-to-deploy commits (left) + job history
          (right). Deploy dabar lives with the list it will ship; one queued/
          running job at a time (single-job guard unchanged). */}
      <section className="bg-white border border-gray-200 rounded-lg px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('findings.deploy.title')}</h2>
        <div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
          {/* Left — Paruošta diegti: the undeployed commits + the deploy button */}
          <div className="min-w-0">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('findings.deploy.ready')}
            </h3>
            {undeployed.length > 0 ? (
              <>
                <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                  {undeployed.map((commit, i) => (
                    <li key={`${commit.hash}-${i}`} className="flex min-w-0 items-baseline gap-2">
                      {commit.hash && (
                        <code className="shrink-0 rounded border border-gray-100 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
                          {commit.hash.slice(0, 7)}
                        </code>
                      )}
                      <span className="truncate text-sm text-gray-700" title={commit.subject}>
                        {commit.subject}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => deployMutation.mutate()}
                  disabled={deployActive || deployMutation.isPending || !hasDeployables || deployTaskQueued}
                  className="mt-3 px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
                >
                  {deployMutation.isPending ? '…' : deployMutation.isSuccess ? '✓ Sukurta' : t('findings.deploy.now')}
                </button>
                {deployMutation.isError && (
                  <p className="mt-1 text-xs text-red-600">{deployMutation.error instanceof Error ? deployMutation.error.message : 'Klaida'}</p>
                )}
                {deployTaskQueued && (
                  <button
                    onClick={() => {
                      const task = allFindings.find(f =>
                        f.status === 'Queued' && f.title.startsWith('ĮDIEGTI:'));
                      if (task) {
                        setProgressOpen(prev => ({ ...prev, [task.id]: true }));
                        document.getElementById(`finding-${task.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                    className="mt-1 text-xs text-[#071E3B] font-medium hover:underline cursor-pointer"
                  >
                    ⚡ Diegimo užduotis jau eilėje — atidaryti →
                  </button>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400">{t('findings.deploy.readyEmpty')}</p>
            )}
            {/* Auto-deploy status (findings 14+15) — a quiet readout of the
                server-owned mode, not a call to action. All: every fixed
                finding ships by itself; Marked: the checkbox marks below;
                Off: no automation. */}
            {autoDeployMode === 'All' ? (
              <div className="mt-3 space-y-0.5">
                <p className="text-sm text-gray-700">
                  <span className="mr-1 text-green-600">✓</span>
                  {t('findings.autoDeploy.modeAll')}
                </p>
                {modeAllWaitingCount > 0 && (
                  <p className="text-xs text-gray-500">
                    {t('findings.autoDeploy.modeAllWaiting', { count: modeAllWaitingCount })}
                  </p>
                )}
                {modeAllFailedCount > 0 && (
                  <p className="text-xs text-amber-600">
                    {t('findings.autoDeploy.modeAllFailed', { count: modeAllFailedCount })}
                  </p>
                )}
              </div>
            ) : autoDeployMode === 'Off' ? (
              <p className="mt-3 text-sm text-gray-400">{t('findings.autoDeploy.modeOff')}</p>
            ) : (
              autoMarked.length > 0 && (
                <div className="mt-3 space-y-0.5">
                  <p className="text-sm text-gray-700">
                    <span className="mr-1 text-green-600">✓</span>
                    {t('findings.autoDeploy.marked', { count: autoMarked.length })}
                  </p>
                  <p className="text-xs text-gray-400">{t('findings.autoDeploy.hint')}</p>
                  {autoWaitingCount > 0 && (
                    <p className="text-xs text-amber-600">
                      {t('findings.autoDeploy.waiting', { count: autoWaitingCount })}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
          {/* Right — Istorija: deploy tasks from the findings registry */}
          <div className="min-w-0">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('findings.deploy.history')}
            </h3>
            {deployTaskHistory.length > 0 ? (
              <ul className="divide-y divide-gray-50">
                {deployTaskHistory.map(task => {
                  const shown = !!outputOpen[task.id] && !!task.progress;
                  return (
                    <li key={task.id} className="py-2 first:pt-0 last:pb-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-[#071E3B]">⚡</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          task.status === 'Done' ? 'bg-green-100 text-green-700'
                          : task.status === 'Failed' ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'}`}
                        >
                          {statusLabel(task.status)}
                        </span>
                        {task.commitHash && (
                          <code className="rounded border border-gray-100 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
                            {task.commitHash.slice(0, 7)}
                          </code>
                        )}
                        <span className="ml-auto text-xs tabular-nums text-gray-400">
                          {jobAgeLabel(task.createdAtUtc, t)}
                        </span>
                        {task.progress && (
                          <button
                            onClick={() => setOutputOpen(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                            aria-expanded={shown}
                            className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:border-gray-500"
                          >
                            {t('findings.progressLabel')} {shown ? '▴' : '▾'}
                          </button>
                        )}
                      </div>
                      {shown && task.progress && (
                        <pre className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs">
                          {task.progress.logTail}
                        </pre>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">
                {deployTaskQueued ? 'Užduotis jau eilėje — istorija atsiras po įdiegimo' : 'Kol kas nėra atliktų diegimų'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Auto-deploy watch bar removed (finding 14 rework): the watch moved
          server-side; its readout lives in the Diegimai left column above. */}

      {isLoading ? (
        <p className="text-sm text-gray-400 py-4">{t('common.loading')}</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                {marksVisible && (
                  <th className="px-2 py-2 w-10">
                    {/* Tri-state select-all over the VISIBLE eligible rows
                        (indeterminate set via ref — not an HTML attribute). */}
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      className="accent-primary-600"
                      aria-label={t('findings.autoDeploy.selectAll')}
                      checked={allEligibleMarked}
                      disabled={autoDeployMutation.isPending}
                      onChange={() => { void toggleSelectAll(); }}
                    />
                  </th>
                )}
                <th className="px-4 py-2 font-semibold">{t('findings.col.title')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.category')}</th>
                <th className="px-4 py-2 font-semibold w-40">{t('findings.col.workspace')}</th>
                <th className="px-4 py-2 font-semibold w-32">{t('findings.col.status')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.priority')}</th>
                <th className="px-4 py-2 font-semibold w-24">{t('findings.col.age')}</th>
                <th className="px-4 py-2 font-semibold w-48">{t('findings.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleFindings.map((f: CopilotFindingDto) => {
                const terminal = f.status === 'Rejected' || f.status === 'Done';
                // Live-work rows carry a commander log to reveal — and since
                // finding 15, so do Failed rows: an AUTO deploy failure
                // surfaces as a Failed finding whose progress.logTail
                // carries the deploy error.
                const progressable =
                  f.status === 'PickedUp' || f.status === 'InProgress' || f.status === 'Failed';
                const progressShown = progressable && !!progressOpen[f.id];
                const progressStale =
                  !!f.progress &&
                  Date.now() - parseAsUtc(f.progress.updatedAtUtc).getTime() > PROGRESS_STALE_MS;
                return (
                  <Fragment key={f.id}>
                  <tr id={`finding-${f.id}`} className="border-t border-gray-50 align-top">
                    {marksVisible && (
                      <td className="px-2 py-2.5 w-10">
                        {/* Closed rows (visible when showClosed) carry their
                            checkbox in the IŠSPRĘSTA section — exactly one
                            checkbox per finding, no duplicate labels.
                            Ineligible rows (ClientPattern lane) get none. */}
                        {!isClosed(f) && isAutoDeployEligible(f) ? (
                          <input
                            type="checkbox"
                            className="accent-primary-600"
                            checked={f.autoDeploy}
                            disabled={autoDeployMutation.isPending}
                            onChange={(e) =>
                              autoDeployMutation.mutate({ id: f.id, autoDeploy: e.target.checked })}
                            aria-label={`${t('findings.autoDeploy.mark')}: ${f.title}`}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <span className={`font-medium ${f.title.startsWith('ĮDIEGTI:') ? 'text-[#071E3B] font-bold' : 'text-gray-900'}`} title={f.description}>{f.title}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      {f.title.startsWith('ĮDIEGTI:') ? (
                        <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-[#071E3B] text-white tracking-wide">
                          ⚡ DIEGYMAS
                        </span>
                      ) : (
                        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${CATEGORY_BADGE[f.category] ?? 'bg-gray-100 text-gray-600'}`}>
                          {catLabel(f.category)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 truncate max-w-[160px]">
                      {f.workspaceName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {statusLabel(f.status)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => priorityMutation.mutate({ id: f.id, priority: 1 })}
                          disabled={priorityMutation.isPending || f.priority <= 1}
                          aria-label="Perkelti į priekį (prioritetas 1)"
                          title="Perkelti į priekį"
                          className="px-2 py-1 text-xs font-bold text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400 disabled:opacity-30"
                        >
                          ⇧
                        </button>
                        <button
                          onClick={() => priorityMutation.mutate({ id: f.id, priority: Math.max(1, f.priority - 1) })}
                          disabled={priorityMutation.isPending || f.priority <= 1}
                          aria-label={t('findings.priorityUp')}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500 disabled:opacity-30"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => priorityMutation.mutate({ id: f.id, priority: Math.min(9999, f.priority + 1) })}
                          disabled={priorityMutation.isPending || f.priority >= 9999}
                          aria-label={t('findings.priorityDown')}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500 disabled:opacity-30"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 tabular-nums">{ageLabel(f.createdAtUtc, t)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        {progressable && (
                          <button
                            onClick={() => setProgressOpen(prev => ({ ...prev, [f.id]: !prev[f.id] }))}
                            aria-expanded={progressShown}
                            className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                          >
                            {t('findings.progressLabel')} {progressShown ? '▴' : '▾'}
                          </button>
                        )}
                        {f.status === 'Registered' && (
                          <button
                            onClick={() => statusMutation.mutate({ id: f.id, next: 'Queued' })}
                            disabled={statusMutation.isPending}
                            className="px-2.5 py-1 text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400"
                          >
                            {t('findings.queue')}
                          </button>
                        )}
                        <button
                          onClick={() => exportFinding(f.id)}
                          className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                        >
                          {t('findings.export')}
                        </button>
                        <button
                          onClick={() => statusMutation.mutate({ id: f.id, next: 'Rejected' })}
                          disabled={terminal || statusMutation.isPending}
                          className="px-2.5 py-1 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:border-red-400 disabled:opacity-30"
                        >
                          {t('findings.reject')}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {progressShown && (
                    <tr className="border-t border-gray-50">
                      <td colSpan={marksVisible ? 8 : 7} className="px-4 py-3">
                        {f.progress ? (
                          <div className="max-w-3xl">
                            <pre className="max-h-40 overflow-y-auto px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 font-mono text-xs whitespace-pre-wrap">
                              {f.progress.logTail}
                            </pre>
                            <p className="mt-1.5 text-xs text-gray-500">
                              {t('findings.progressAgo', { age: progressAgeLabel(f.progress.updatedAtUtc) })}
                            </p>
                            {progressStale && (
                              <p className="mt-1 text-xs font-medium text-amber-700">
                                {t('findings.progressStale')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">{t('findings.progressEmpty')}</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {(findings ?? []).length === 0 && (
            <p className="text-sm text-gray-400 px-4 py-6 text-center">{t('findings.empty')}</p>
          )}
        </div>
      )}

      {/* IŠSPRĘSTA — the solved history as its own section below the active
          list (mirrors Diegimai's Istorija): always visible when non-empty,
          collapsed by default, one click to review what the agents shipped. */}
      {!isLoading && closedCount > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg">
          <button
            onClick={() => setShowClosed(prev => !prev)}
            aria-expanded={showClosed}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left"
          >
            <span className="text-sm font-semibold text-gray-700">
              {t('findings.solvedTitle')} ({closedCount})
            </span>
            <span className="text-xs text-gray-400">
              {showClosed ? `↑ ${t('findings.hideClosed')}` : t('findings.showClosed', { count: closedCount })}
            </span>
          </button>
          {showClosed && (
            <table className="w-full text-sm border-t border-gray-100">
              <tbody>
                {allFindings.filter(isClosed).map((f: CopilotFindingDto) => (
                  <tr key={f.id} className="border-t border-gray-50">
                    {marksVisible && (
                      <td className="px-2 py-2 w-10">
                        {/* Fixed + eligible = waiting to ship — the key state to
                            mark for auto-deploy. Done/Rejected already shipped. */}
                        {isAutoDeployEligible(f) && (
                          <input
                            type="checkbox"
                            className="accent-primary-600"
                            checked={f.autoDeploy}
                            disabled={autoDeployMutation.isPending}
                            onChange={(e) =>
                              autoDeployMutation.mutate({ id: f.id, autoDeploy: e.target.checked })}
                            aria-label={`${t('findings.autoDeploy.mark')}: ${f.title}`}
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2 w-10">
                      <span className="text-green-600 text-xs">✓</span>
                    </td>
                    <td className="px-2 py-2">
                      <div className="text-gray-600">{f.title}</div>
                      {f.commitHash && (
                        <code className="text-[10px] text-gray-400 font-mono">{f.commitHash}</code>
                      )}
                    </td>
                    <td className="px-4 py-2 w-32">
                      <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${DEPLOY_STATUS_CHIP[f.status === 'Done' ? 'Succeeded' : 'Succeeded'] ?? 'bg-green-100 text-green-700'}`}>
                        {t(`findings.status.${f.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-2 w-24 text-xs text-gray-400">
                      {f.updatedAtUtc ? jobAgeLabel(f.updatedAtUtc, t) : ageLabel(f.createdAtUtc, t)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
